import { useAuthStore } from '../stores/authStore';

const API_BASE = import.meta.env.PROD
  ? 'https://api.lifespark360.com/v1'
  : '/v1';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
}

class ApiClient {
  async request<T>(
    path: string,
    options: RequestInit = {},
  ): Promise<T> {
    const { accessToken, refresh } = useAuthStore.getState();

    const headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Don't set Content-Type for FormData (browser sets it with boundary)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
    });

    // Handle 401 - try refresh
    if (res.status === 401 && accessToken) {
      const refreshed = await refresh();
      if (refreshed) {
        const newToken = useAuthStore.getState().accessToken;
        headers['Authorization'] = `Bearer ${newToken}`;
        const retryRes = await fetch(`${API_BASE}${path}`, {
          ...options,
          headers,
        });
        const retryData: ApiResponse<T> = await retryRes.json();
        if (!retryData.success) {
          throw new Error(retryData.error?.message || 'Request failed');
        }
        return retryData.data as T;
      }
    }

    const data: ApiResponse<T> = await res.json();
    if (!data.success) {
      throw new Error(data.error?.message || 'Request failed');
    }
    return data.data as T;
  }

  get<T>(path: string) {
    return this.request<T>(path);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : JSON.stringify(body),
    });
  }

  patch<T>(path: string, body: unknown) {
    return this.request<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  delete<T>(path: string) {
    return this.request<T>(path, { method: 'DELETE' });
  }
}

export const api = new ApiClient();
